import React from 'react';

export function LauncherPanel({ title, count, children, className = '' }) {
  return (
    <details className={`phone-remote__launcher-section ${className}`}>
      <summary className="phone-remote__launcher-header">
        <strong>{title}</strong><span>{count}</span>
      </summary>
      <div className="phone-remote__launcher-content">{children}</div>
    </details>
  );
}

export function groupAreaLauncherTargets(targets, devices) {
  const groups = new Map();
  targets.forEach((target) => {
    const app = /suitestream/i.test(`${target.appName || ''} ${target.packageName || ''}`)
      ? 'Suitestream' : target.appName || target.packageName || 'Other';
    const key = target.packageName || app;
    if (!groups.has(key)) groups.set(key, { app, items: new Map() });
    const routes = devices.filter((device) => Number(device.appControlDeviceId || device.target.id) === Number(target.deviceId)
      && device.switchReady && device.links.some((link) => Number(link.id) === Number(target.id)));
    const entry = routes.find((route) => !route.switchRequired) || routes[0] || null;
    const item = { target, entry };
    // Area saves can create a copy on each player. Prefer a ready local copy for one-tap playback.
    const identity = target.seed?.contentId
      ? `${target.kind}:${target.seed.provider || ''}:${target.seed.contentId}` : `target:${target.id}`;
    const items = groups.get(key).items;
    const current = items.get(identity);
    if (!current || (item.entry && (!current.entry || (current.entry.switchRequired && !item.entry.switchRequired)))) {
      items.set(identity, item);
    }
  });
  return Array.from(groups.values()).map((group) => ({ app: group.app,
    items: Array.from(group.items.values()).sort((a, b) => a.target.name.localeCompare(b.target.name))
  })).sort((a, b) => a.app.localeCompare(b.app));
}

function launchClass(entry) {
  return !entry?.switchReady ? 'phone-remote__launch--unavailable'
    : entry.switchRequired ? 'phone-remote__launch--switch' : 'phone-remote__launch--local';
}

export default function LauncherPanels({ devices, playlists, contentTargets, status, busyKey, blocked, onLaunch }) {
  const ready = status === 'ready';
  const appsCount = (entries) => entries.some((entry) => entry.appsStatus === 'error')
    ? 'Unavailable' : entries.reduce((sum, entry) => sum + entry.apps.length + (entry.assignedDestinations || []).filter((item) => !item.id.split(':')[3]).length + (entry.suitestreamApp ? 1 : 0), 0);
  const groups = groupAreaLauncherTargets(contentTargets.filter((item) => item.kind === 'playlist'), devices);
  const targetGroups = groupAreaLauncherTargets(contentTargets.filter((item) => item.kind !== 'playlist'), devices);
  const suitestreamEntry = devices.find((entry) => entry.suitestreamApp && entry.switchReady && !entry.switchRequired)
    || devices.find((entry) => entry.suitestreamApp && entry.switchReady);
  const renderTargets = (group, playlistsOnly = false) => (
    <LauncherPanel key={group.app} title={`${group.app}${playlistsOnly ? ' Playlists' : ''}`} count={group.items.length}>
      <div className="phone-remote__app-grid">
        {group.items.map(({ target, entry }) => (
          <button key={target.id} type="button" className={`phone-remote__app-btn ${launchClass(entry)}`}
            disabled={blocked || !entry} onClick={() => onLaunch(entry, { kind: 'link', id: target.id, label: target.name })}>
            <strong>{busyKey === `${entry?.target.id}:link:${target.id}` ? 'Starting…' : target.name}</strong>
            <span>{entry ? `${entry.target.name}${entry.switchRequired ? ' · Switch + play' : ' · Play now'}` : 'Unavailable on this screen'}</span>
          </button>
        ))}
      </div>
    </LauncherPanel>
  );
  return (
    <>
      <LauncherPanel title="Saved Playlists" count={ready ? `${playlists.length + groups.reduce((sum, group) => sum + group.items.length, 0)} in this area` : 'Loading…'}>
        {playlists.length ? <LauncherPanel title="Suitestream Playlists" count={playlists.length}>
          <div className="phone-remote__app-grid">
            {playlists.map((playlist) => {
              const entry = playlist.mediaType === 'video' ? suitestreamEntry : null;
              return <button key={playlist.id} type="button" className={`phone-remote__app-btn ${launchClass(entry)}`}
                disabled={blocked || !entry || !playlist.itemCount}
                onClick={() => onLaunch(entry, { kind: 'playlist', id: playlist.id, label: playlist.name })}>
                <strong>{busyKey === `${entry?.target.id}:playlist:${playlist.id}` ? 'Starting…' : playlist.name}</strong>
                <span>{!playlist.itemCount ? 'Empty playlist' : entry ? `${entry.target.name} · ${entry.switchRequired ? 'Switch + play' : 'Play now'}` : 'No compatible player on this screen'}</span>
              </button>;
            })}
          </div>
        </LauncherPanel> : null}
        {groups.map((group) => renderTargets(group, true))}
        {ready && !playlists.length && !groups.length ? <div className="phone-remote__notice">No saved playlists in this area.</div> : null}
      </LauncherPanel>
      <LauncherPanel title="Apps" count={ready ? appsCount(devices) : 'Loading…'}>
        {[false, true].map((other) => (
          <LauncherPanel key={String(other)} title={other ? 'Apps on other devices' : 'Apps on this device'}
            count={ready ? appsCount(devices.filter((entry) => entry.switchRequired === other)) : 'Loading…'}>
            {devices.filter((entry) => entry.switchRequired === other).map((entry) => (
              <div key={entry.target.id} className="phone-remote__launcher-device">
                <div className="phone-remote__launcher-device-heading"><strong>{entry.target.name}</strong>
                  <span>{entry.appsStatus === 'error' ? 'Apps unavailable' : entry.switchRequired ? entry.switchReady ? `Switches to ${entry.expectedInput || 'this source'}` : 'Input setup required' : 'Checks input before launch'}</span></div>
                {entry.appsStatus === 'error' ? <div className="phone-remote__notice">
                  {entry.appsMessage} Refresh the launcher to retry.
                </div> : null}
                {entry.assignedError && <div className="phone-remote__notice">{entry.assignedError}</div>}
                <div className="phone-remote__app-grid">
                  {(entry.assignedDestinations || []).map((destination) => <button key={destination.id} type="button"
                    className="phone-remote__app-btn" disabled={blocked || !destination.ready}
                    onClick={() => onLaunch(entry, { kind: 'assigned', id: destination.id, label: destination.label, shared: destination.shared })}>
                    <strong>{destination.label}</strong><span>{destination.ready ? 'Prepare destination' : 'Needs setup'}</span>
                  </button>)}
                  {[...(entry.suitestreamApp ? [{ ...entry.suitestreamApp, suitestream: true }] : []), ...entry.apps].map((app) => (
                    <button key={app.package_name} type="button" className={`phone-remote__app-btn ${launchClass(entry)}`}
                      disabled={blocked || !entry.switchReady}
                      onClick={() => onLaunch(entry, { kind: app.suitestream ? 'suitestream' : 'app', packageName: app.package_name,
                        label: app.suitestream ? 'Suitestream' : app.app_name || app.package_name })}>
                      <strong>{busyKey === `${entry.target.id}:${app.suitestream ? 'suitestream' : 'app'}:${app.package_name}` ? 'Starting…'
                        : app.suitestream ? 'Suitestream' : app.app_name || app.package_name}</strong>
                      <span>{entry.switchRequired ? 'Switch + launch' : 'Launch now'}</span>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            {ready && !devices.some((entry) => entry.switchRequired === other && (entry.appsStatus === 'error' || entry.apps.length || entry.suitestreamApp))
              ? <div className="phone-remote__notice">No apps available.</div> : null}
          </LauncherPanel>
        ))}
      </LauncherPanel>
      {targetGroups.length ? <LauncherPanel title="Saved Targets" count={targetGroups.reduce((sum, group) => sum + group.items.length, 0)}>
        {targetGroups.map((group) => renderTargets(group))}
      </LauncherPanel> : null}
    </>
  );
}
